import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { cn } from "@/lib/utils";
import { memo } from "react";

// Define the variant types
type DataTableVariant = "default" | "grid" | "zebra";

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  className?: string;
  variant?: DataTableVariant;
}

// Add type for column meta
type ColumnMeta = {
  className?: string;
};

function DataTableComponent<TData, TValue>({
  columns,
  data,
  className,
  variant = "default",
}: DataTableProps<TData, TValue>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Define class names based on variant
  const tableClasses = cn({
    "border-collapse": variant === "grid",
    "[&_th]:border-b [&_td]:border-b":
      variant === "default" || variant === "zebra",
    "[&_th]:border [&_td]:border [&_th]:border-border [&_td]:border-border":
      variant === "grid",
  });

  const containerClasses = cn(
    "rounded-md",
    {
      border: variant === "default" || variant === "zebra",
      "border-0": variant === "grid",
    },
    className,
  );

  return (
    <div className={containerClasses}>
      <Table className={tableClasses}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    className={cn(
                      {
                        "bg-muted": variant === "grid" || variant === "zebra",
                      },
                      (header.column.columnDef.meta as ColumnMeta)?.className,
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row, index) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
                className={cn({
                  "bg-muted/50": variant === "zebra" && index % 2 === 1,
                })}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={
                      (cell.column.columnDef.meta as ColumnMeta)?.className
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export const DataTable = memo(DataTableComponent) as typeof DataTableComponent;
